import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { z } from "zod";

import {
  getCategoriesOfLifeFn,
  deleteCategoryOfLifeAndFileFn,
  finalizeCategoryFn,
  updateCategoryFn,
  getCategoryUploadUrlFn,
  getCategoryByIdFn,
  getSharedCategoriesFn,
  getSharedCategoryByIdFn,
} from "~/lib/functions/category-of-life";
import type {
  CreateCategoryOfLifeSchema,
  UpdateCategoryOfLifeWithImageSchema,
  GetCategoryOfLifeUrlSchema,
} from "~/lib/schemas/category-of-life";

export const categoryQueryKeys = {
  all: ["categories"] as const,
  list: () => [...categoryQueryKeys.all, "list"] as const,
  categoriesOfLife: () =>
    [...categoryQueryKeys.all, "categories-of-life"] as const,
  sharedCategories: (userId: string) =>
    [...categoryQueryKeys.all, "shared-categories", { userId }] as const,
  detail: (categoryId: string) =>
    [...categoryQueryKeys.all, "detail", { categoryId }] as const,
  sharedDetail: (categoryId: string) =>
    [...categoryQueryKeys.all, "shared-detail", { categoryId }] as const,
};

export const categoriesOfLifeQueryOptions = () => ({
  queryKey: categoryQueryKeys.categoriesOfLife(),
  queryFn: () => getCategoriesOfLifeFn(),
});

export const useCategoriesOfLife = () => {
  return useSuspenseQuery(categoriesOfLifeQueryOptions());
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: string) =>
      deleteCategoryOfLifeAndFileFn({
        data: { id: categoryId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all });
    },
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: z.infer<typeof CreateCategoryOfLifeSchema>) =>
      finalizeCategoryFn({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: z.infer<typeof UpdateCategoryOfLifeWithImageSchema>) =>
      updateCategoryFn({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all });
    },
  });
};

export const useGetCategoryUploadUrl = () => {
  return useMutation({
    mutationFn: (data: z.infer<typeof GetCategoryOfLifeUrlSchema>) =>
      getCategoryUploadUrlFn({ data }),
  });
};

export const categoryByIdQueryOptions = (categoryId: string) => ({
  queryKey: categoryQueryKeys.detail(categoryId),
  queryFn: () => getCategoryByIdFn({ data: { id: categoryId } }),
  enabled: !!categoryId,
});

export const useCategoryById = (categoryId: string) => {
  return useSuspenseQuery(categoryByIdQueryOptions(categoryId));
};

export const sharedCategoriesQueryOptions = (userId: string | undefined) => ({
  queryKey: categoryQueryKeys.sharedCategories(userId ?? ""),
  queryFn: () => getSharedCategoriesFn({ data: { userId: userId ?? "" } }),
  enabled: !!userId,
});

export const useSharedCategories = (userId: string | undefined) => {
  return useQuery(sharedCategoriesQueryOptions(userId));
};

export const sharedCategoryByIdQueryOptions = (categoryId: string) => ({
  queryKey: categoryQueryKeys.sharedDetail(categoryId),
  queryFn: () => getSharedCategoryByIdFn({ data: { id: categoryId } }),
  enabled: !!categoryId,
});

export const useSharedCategoryById = (categoryId: string) => {
  return useSuspenseQuery(sharedCategoryByIdQueryOptions(categoryId));
};
